import { useState } from 'react';
import { Download, Share, Plus, X } from 'lucide-react';
import { useInstallPrompt } from '../lib/pwa';
import { Button } from './charte';
import { Sheet } from './shell/Sheet';

const DISMISS_KEY = 'pmd-moniteur-install-dismissed';

function wasDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Bannière « Télécharger l'app » : bouton d'installation natif sur Android/Chrome
 * (via `beforeinstallprompt`) et, sur iOS, instructions pour l'ajout à l'écran
 * d'accueil (Safari n'expose pas d'installation programmable).
 */
export default function InstallAppBanner() {
  const { canInstall, promptInstall, installed, ios } = useInstallPrompt();
  const [dismissed, setDismissed] = useState<boolean>(wasDismissed);
  const [iosOpen, setIosOpen] = useState(false);

  const dismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // stockage indisponible : on masque juste pour la session.
    }
  };

  // Rien à proposer si déjà installée, ou si aucun canal d'installation.
  if (installed || dismissed) return null;
  if (!canInstall && !ios) return null;

  return (
    <>
      <div className="border-b border-primary-100 bg-surface-indigo">
        <div className="mx-auto flex max-w-6xl items-center gap-3 px-4 py-2.5 sm:px-6">
          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-xl bg-white text-primary shadow-sm">
            <Download size={16} aria-hidden />
          </span>
          <p className="min-w-0 flex-1 text-sm font-medium text-body">Installez l'application Permio sur votre appareil</p>
          <Button variant="primary" size="sm" onClick={() => (canInstall ? void promptInstall() : setIosOpen(true))}>
            Télécharger l'app
          </Button>
          <Button variant="ghost" size="sm" iconOnly icon={X} aria-label="Fermer" onClick={dismiss} />
        </div>
      </div>

      {/* Instructions iOS */}
      {iosOpen && (
        <Sheet
          title="Installer l'application"
          onClose={() => setIosOpen(false)}
          footer={
            <Button variant="primary" className="w-full" onClick={() => setIosOpen(false)}>
              J'ai compris
            </Button>
          }
        >
          <ol className="space-y-4 text-sm text-body">
              <li className="flex items-center gap-3">
                <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-primary-50 font-semibold text-primary-700">1</span>
                <span className="flex items-center gap-1.5">
                  Touchez <Share size={16} className="text-primary" /> « Partager » dans la barre Safari
                </span>
              </li>
              <li className="flex items-center gap-3">
                <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-primary-50 font-semibold text-primary-700">2</span>
                <span className="flex items-center gap-1.5">
                  Choisissez <Plus size={16} className="text-primary" /> « Sur l'écran d'accueil »
                </span>
              </li>
              <li className="flex items-center gap-3">
                <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-primary-50 font-semibold text-primary-700">3</span>
                <span>Validez : l'app apparaît sur votre écran d'accueil.</span>
              </li>
          </ol>
        </Sheet>
      )}
    </>
  );
}
